import { useContext } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { TComment } from '@modules/comments';
import { updateCommentQueryData } from '@modules/comments/api';
import type { TUseDocumentResult } from '@modules/documents';
import type { TUseFileResult } from '@modules/files';
import { ApiContext } from '@shared/contexts';
import { getQueryKey } from '@shared/helpers';
import { docOrFileKey } from '../helpers/itemKeys.helper';
import { EPeriodicReviewOutcome } from '../types/periodicReviews.types';
import type { TPeriodicReview, TPeriodicReviewItemType, TPeriodicReviewUpdate } from '../types/periodicReviews.types';
import invalidateFolderCardStatusQueries from './folderCardStatusQueries';

type TCompletePeriodicReviewResult = {
  periodicReview: TPeriodicReview;
  comment?: TComment;
};

const patchPeriodicReview = (
  queryClient: QueryClient,
  itemType: TPeriodicReviewItemType,
  id: string,
  periodicReview: TPeriodicReview
) => {
  const itemKey = docOrFileKey(itemType);

  queryClient.setQueryData(
    [getQueryKey(itemType, id)],
    (oldData: TUseDocumentResult | TUseFileResult | undefined): TUseDocumentResult | TUseFileResult | undefined => {
      if (!oldData) return oldData;

      const item = (oldData as TUseDocumentResult).doc ?? (oldData as TUseFileResult).file;
      return {
        ...oldData,
        [itemKey]: {
          ...item,
          periodicReview,
        },
      } as TUseDocumentResult | TUseFileResult;
    }
  );
};

export const useSetPeriodicReviewEnabled = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    id: string;
    itemType: TPeriodicReviewItemType;
    enabled: boolean;
  }): Promise<TPeriodicReview> =>
    await apiClient
      .put(`/items/${variables.id}/periodic-review/${variables.itemType}/enabled`, { enabled: variables.enabled })
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (periodicReview, variables) => {
      patchPeriodicReview(queryClient, variables.itemType, variables.id, periodicReview);
      invalidateFolderCardStatusQueries(queryClient, variables.id, variables.itemType);
      // Enabling opens the settings dialog next, so only the disable path needs a toast.
      if (!variables.enabled) show('success', 'Periodic review disabled.');
    },
    onError: (error, variables, context) =>
      handleApiError(
        variables.enabled ? 'Enabling periodic review failed!' : 'Disabling periodic review failed!',
        true
      )(error, variables, context),
  });
};

export const useUpdatePeriodicReview = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    id: string;
    itemType: TPeriodicReviewItemType;
    update: TPeriodicReviewUpdate;
  }): Promise<TPeriodicReview> =>
    await apiClient
      .put(`/items/${variables.id}/periodic-review/${variables.itemType}`, variables.update)
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (periodicReview, variables) => {
      patchPeriodicReview(queryClient, variables.itemType, variables.id, periodicReview);
      invalidateFolderCardStatusQueries(queryClient, variables.id, variables.itemType);
      show('success', 'Periodic review saved.');
    },
    onError: handleApiError('Saving periodic review failed!', true),
  });
};

export const useCompletePeriodicReview = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    id: string;
    itemType: TPeriodicReviewItemType;
    outcome: EPeriodicReviewOutcome;
    note?: string;
    addNoteAsComment: boolean;
  }): Promise<TCompletePeriodicReviewResult> =>
    await apiClient
      .post(`/items/${variables.id}/periodic-review/${variables.itemType}/complete`, {
        outcome: variables.outcome,
        note: variables.note,
        addNoteAsComment: variables.addNoteAsComment,
      })
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: ({ periodicReview, comment }, variables) => {
      show('success', 'Review completed.');
      patchPeriodicReview(queryClient, variables.itemType, variables.id, periodicReview);
      invalidateFolderCardStatusQueries(queryClient, variables.id, variables.itemType);
      if (comment) updateCommentQueryData(queryClient, variables.id, variables.itemType, 'ADD', comment._id, comment);
    },
    onError: handleApiError('Completing periodic review failed!', true),
  });
};
